import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Calendar,
  Clock,
  CheckCircle,
  AlertTriangle,
  Eye,
  Search,
  DollarSign,
  User,
  ArrowLeft,
  X,
  RefreshCw,
  TrendingUp,
  Flame
} from 'lucide-react';
import { bondsAPI, tokenTransfersAPI, subscriptionAPI, walletAPI, maturityApprovalsAPI, formatCurrency, formatDate } from '../../services/api';
import { getBondTokenAddress } from '../../utils/bondUtils';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { BOND_STATUS } from '../../constants/subscription.constants';
import { useAuth } from '../../contexts/AuthContext';

// Simple Ethereum address validator (0x + 40 hex chars)
const isValidEthereumAddress = (addr) => {
  if (!addr || typeof addr !== 'string') return false;
  const a = addr.trim();
  return /^0x[a-fA-F0-9]{40}$/.test(a);
};

// Simple MongoDB ObjectId validator (24 hex chars)
const isValidObjectId = (id) => {
  if (!id || typeof id !== 'string') return false;
  return /^[0-9a-fA-F]{24}$/.test(id);
};

const EarlyRedemption = () => {
  // This component now uses the dedicated /api/bonds/maturity endpoint
  // which returns only bonds where current date < maturity date
  // This improves performance and reduces unnecessary frontend filtering

  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const queryClient = useQueryClient();

  const [selectedBond, setSelectedBond] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRedemptionModal, setShowRedemptionModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  // Generic helpers to replace window.alert usage with modals
  const showSuccess = (msg) => { setSuccessMessage(msg); setShowSuccessModal(true); };
  const showError = (msg) => { setErrorMessage(msg); setShowErrorModal(true); };

  // State for token transfers by bond
  const [bondTokenTransfers, setBondTokenTransfers] = useState({});
  const [loadingTokenTransfers, setLoadingTokenTransfers] = useState(false);

  // Redemption form state
  const [redemptionType, setRedemptionType] = useState('Maturity');
  const [redemptionDate, setRedemptionDate] = useState('');
  // Token address is always sourced from bond data now; no manual resolution or override state

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [filteredBondsInfo, setFilteredBondsInfo] = useState({ total: 0, excluded: 0, reason: '' });

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  // Maturity approval states cache { bondId: { redemption: {...}, burn: {...} } }
  const [maturityApprovals, setMaturityApprovals] = useState({});
  const [loadingApprovals, setLoadingApprovals] = useState(false);
  // Track auto-creation of redemption transfers per bond
  const [creatingRedemptionTransfers, setCreatingRedemptionTransfers] = useState({});
  // Idempotency locks to ensure we don't create redemption transfers more than once per bond
  const [redemptionCreationLocks, setRedemptionCreationLocks] = useState({});
  // Explicit per-bond creation status: idle|creating|success|zero|failed
  const [redemptionCreationStatus, setRedemptionCreationStatus] = useState({});
  // Track bond redemption records (for payment reference check)
  const [bondRedemptions, setBondRedemptions] = useState({});

  // Fetch bonds data from real API
  const { data: bondsResponse, isLoading, error, refetch } = useQuery({
    queryKey: ['bonds', 'maturity', refreshTrigger, page, pageSize],
    queryFn: async () => {
      console.log('🔍 Fetching bonds for maturity management...', { page, pageSize });
      try {
        // Use the new maturity-specific endpoint with pagination params
        const response = await bondsAPI.getMaturityBonds({
          page,
          pageSize,
          limit: pageSize // some APIs use limit instead of pageSize
        });
        console.log('📊 Maturity bonds API response:', response);
        console.log('📊 Full response structure:', JSON.stringify(response, null, 2));

        // If API returns empty, just return empty array
        const bonds = response?.data?.data?.bonds || response?.data?.bonds || response?.bonds || response?.data || [];
        if (bonds.length === 0) {
          console.log('⚠️ API returned no maturity bonds');
        } else {
          console.log('✅ Retrieved bonds:', bonds.length);
        }

        return response;
      } catch (apiError) {
        console.error('❌ Maturity bonds API failed:', apiError);

        // Return empty bonds array if API fails
        return { data: { bonds: [] } };
      }
    },
    select: data => {
      console.log('🎯 Raw API response structure:', data);

      // Parse the axios response structure
      let bonds = [];
      let paginationMeta = {};

      // First, extract the actual data from the axios response
      const responseData = data.data || data;
      console.log('📦 Response data after extraction:', responseData);

      // Extract pagination metadata
      if (responseData && responseData.data) {
        paginationMeta = {
          total: responseData.data.total || 0,
          totalPages: responseData.data.totalPages || 1,
          page: responseData.data.page || 1,
          pageSize: responseData.data.pageSize || 20
        };
        console.log('📊 Extracted pagination metadata:', paginationMeta);
      }

      if (responseData && responseData.data && responseData.data.bonds) {
        bonds = responseData.data.bonds;
        console.log('✅ Found bonds in responseData.data.bonds:', bonds.length);
      } else if (responseData && responseData.bonds) {
        bonds = responseData.bonds;
        console.log('✅ Found bonds in responseData.bonds:', bonds.length);
      } else if (responseData && Array.isArray(responseData)) {
        bonds = responseData;
        console.log('✅ Found bonds as direct array:', bonds.length);
      } else {
        console.log('❌ Could not find bonds in response. ResponseData structure:', Object.keys(responseData || {}));
        console.log('❌ Full responseData:', responseData);
        bonds = [];
      }

      console.log('All bonds from API:', bonds.length);

      if (bonds.length > 0) {
        console.log('Bond statuses:', bonds.slice(0, 5).map(b => ({ name: b.bondName, status: b.status })));
        console.log('Sample bond structure:', bonds[0]);
      } else {
        console.log('❗ No bonds found in API response.');
      }

      // Since the backend now filters for maturity management (current date < maturity date),
      // we just need to validate bonds have valid IDs and apply any additional status filtering
      const filtered = bonds.filter(bond => {
        if (!bond) return false;

        // First check if bond has a valid ID (required for API operations)
        const hasValidId = bond._id || bond.id;
        if (!hasValidId) {
          console.log(`❌ Excluding bond ${bond.bondName || 'Unknown'}: no valid ID found`);
          return false;
        }

        // The backend already filtered for bonds where current date < maturity date
        // So we include all bonds from the API response
        console.log(`✅ Including bond ${bond.bondName || 'Unknown'}: status=${bond.status}`);
        return true;
      }); console.log('Filtered bonds:', filtered.length);

      // Sort bonds by maturity date in ascending order (closest maturity date first)
      const sortedBonds = filtered.sort((a, b) => {
        const dateA = a.maturityDate ? new Date(a.maturityDate) : new Date('9999-12-31');
        const dateB = b.maturityDate ? new Date(b.maturityDate) : new Date('9999-12-31');
        return dateA - dateB; // Ascending order (earliest/closest maturity first)
      });

      console.log('Sorted bonds by maturity date (closest first):', sortedBonds.slice(0, 3).map(b => ({
        name: b.bondName,
        status: b.status,
        maturityDate: b.maturityDate
      })));

      // Return both bonds and pagination metadata
      return {
        bonds: sortedBonds,
        pagination: paginationMeta
      };
    },
    // Auto refresh disabled per requirement
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false, // Don't retry API calls, use demo data instead
  });

  const bonds = bondsResponse?.bonds || [];

  // Extract pagination metadata from bondsResponse
  useEffect(() => {
    if (bondsResponse?.pagination) {
      const { total: totalCount, totalPages: totalPagesCount } = bondsResponse.pagination;

      console.log('📊 Setting pagination state from select:', {
        total: totalCount,
        totalPages: totalPagesCount,
        currentPage: page,
        pageSize: pageSize
      });

      setTotal(totalCount);
      setTotalPages(totalPagesCount);
    }
  }, [bondsResponse, pageSize, page]);

  // Note: Removed burn requests query - now using token transfer status instead

  // Note: Removed redemption status fetching - now using token transfer status instead

  // Function to fetch token transfers for multiple bonds in a single batched request
  // Uses backend support for bondIds comma separated list: /api/token-transfers?bondIds=id1,id2
  const fetchTokenTransfers = async (bondIds) => {
    if (!bondIds || bondIds.length === 0) {
      console.log('No bond IDs provided for token transfers fetch');
      return;
    }

    // Remove already fresh (fetched in last 15s) bondIds to avoid redundant calls
    const now = Date.now();
    const FRESH_WINDOW = 15_000; // 15 seconds
    const idsNeedingFetch = bondIds.filter(id => {
      const existing = bondTokenTransfers[id];
      if (!existing) return true;
      return !existing.lastFetched || (now - new Date(existing.lastFetched).getTime()) > FRESH_WINDOW;
    });

    if (idsNeedingFetch.length === 0) {
      console.log('All bond token transfers are fresh; skipping fetch.');
      return;
    }

    console.log('� Batch fetching token transfers for bonds:', idsNeedingFetch);
    setLoadingTokenTransfers(true);

    try {
      // Backend limit guard: slice to 100 to prevent overly large query param
      const batchIds = idsNeedingFetch.slice(0, 100);
      const param = batchIds.join(',');
      // Use cursor pagination to stream batches until no next or max batches reached
      let after; let aggregated = []; let loops = 0; const MAX_LOOPS = 5; // safeguard
      do {
        const response = await tokenTransfersAPI.getTokenTransfersCursor({
          bondIds: param,
          limit: 100,
          includeBondDetails: true,
          includeUserDetails: true,
          sortBy: 'createdAt',
          sortOrder: 'desc',
          after
        });
        aggregated.push(...(response.transfers || []));
        after = response.endCursor;
        if (!response.hasNextPage) break;
        loops += 1;
      } while (loops < MAX_LOOPS);
      const response = { data: { data: aggregated } };

      // Normalize result list of transfers
      let transfers = aggregated;

      // Group by bondId
      const grouped = transfers.reduce((acc, t) => {
        let rawBondId = t.bondId;
        // If populated, bondId will be an object; pick its _id/id
        if (rawBondId && typeof rawBondId === 'object') {
          rawBondId = rawBondId._id || rawBondId.id;
        }
        // Fallback to embedded bond object if present
        if (!rawBondId && t.bond) {
          rawBondId = t.bond._id || t.bond.id;
        }
        const bId = rawBondId ? String(rawBondId) : null;
        if (!bId) return acc;
        if (!acc[bId]) acc[bId] = [];
        acc[bId].push(t);
        return acc;
      }, {});

      // Merge into existing cache
      setBondTokenTransfers(prev => {
        const updated = { ...prev };
        batchIds.forEach(id => {
          updated[id] = {
            transfers: grouped[id] || [],
            lastFetched: new Date(),
            error: null
          };
        });
        return updated;
      });

      console.log('✅ Batch token transfers updated. Counts:', Object.fromEntries(Object.entries(grouped).map(([k, v]) => [k, v.length])));
    } catch (error) {
      console.error('❌ Error in batched fetchTokenTransfers:', error);
      // Mark each requested bond with error without clearing others
      setBondTokenTransfers(prev => {
        const updated = { ...prev };
        idsNeedingFetch.forEach(id => {
          updated[id] = updated[id] || {};
          updated[id].error = error.message || 'Fetch failed';
          updated[id].lastFetched = new Date();
          updated[id].transfers = updated[id].transfers || [];
        });
        return updated;
      });
    } finally {
      setLoadingTokenTransfers(false);
    }
  };

  // Debounced refresh to avoid rapid multiple triggers
  const debouncedFetchTokenTransfers = (() => {
    let timeout; return (ids) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fetchTokenTransfers(ids), 250);
    };
  })();

  // Helper function to calculate days to maturity
  const calculateDaysToMaturity = (maturityDate) => {
    if (!maturityDate) return null;

    const currentDate = new Date();
    const target = new Date(maturityDate);
    const diffTime = target.getTime() - currentDate.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Helper function to check if payment reference exists for a bond
  const hasPaymentReferenceForBond = (bond) => {
    if (!bond) return false;
    const bondId = bond._id || bond.id;
    
    // Check bond object directly
    if (bond.hasPaymentReference || 
        (bond.paymentReferenceFiles && bond.paymentReferenceFiles.length > 0)) {
      return true;
    }
    
    // Check redemption nested object
    if (bond.redemption && bond.redemption.paymentReferenceFiles && 
        bond.redemption.paymentReferenceFiles.length > 0) {
      return true;
    }
    
    // Check fetched redemption data
    const redemption = bondRedemptions[bondId];
    if (redemption && redemption.paymentReferenceFiles && 
        redemption.paymentReferenceFiles.length > 0) {
      return true;
    }
    
    return false;
  };

  // Helper functions to check token transfer statuses for bonds

  // Get token transfers for a specific bond and type
  const getBondTokenTransfers = (bondId, transferType = null) => {
    if (!bondId || !bondTokenTransfers[bondId]) {
      return [];
    }

    const bondData = bondTokenTransfers[bondId];
    let transfers = bondData.transfers || [];

    if (transferType) {
      transfers = transfers.filter(transfer =>
        transfer.transferType?.toLowerCase() === transferType.toLowerCase()
      );
    }

    return transfers;
  };

  // Resolve a token contract address for a bond using multiple fallbacks
  // No resolver needed; bond must carry tokenDetails.tokenAddress

  // Check if bond has redemption token transfers
  const hasRedemptionTokenTransfers = (bondId) => {
    const redemptionTransfers = getBondTokenTransfers(bondId, 'Redemption');
    return redemptionTransfers.length > 0;
  };

  // --- Status helpers (normalize multi-stage statuses) ---
  const isApprovedLike = (s) => {
    const v = String(s || '').toLowerCase();
    return v === 'approved' || v === 'completed';
  };
  const isPendingLike = (s) => {
    const v = String(s || '').toLowerCase();
    // Matches: Pending, Pending L1, Pending L2, Processing
    return v.startsWith('pending') || v === 'processing';
  };

  // Check if redemption token transfers are approved
  const hasApprovedRedemptionTransfers = (bondId) => {
    const redemptionTransfers = getBondTokenTransfers(bondId, 'Redemption');
    return redemptionTransfers.some(t => isApprovedLike(t.status));
  };

  // All redemption transfers must be approved/completed (and at least one exists)
  const areAllRedemptionTransfersApproved = (bondId) => {
    if (!bondId) return false;
    const redemptionTransfers = getBondTokenTransfers(bondId, 'Redemption');
    if (!redemptionTransfers || redemptionTransfers.length === 0) return false;
    return redemptionTransfers.every(t => isApprovedLike(t.status));
  };

  // Check if redemption token transfers are pending (any pending-like status)
  const hasPendingRedemptionTransfers = (bondId) => {
    const redemptionTransfers = getBondTokenTransfers(bondId, 'Redemption');
    return redemptionTransfers.some(t => isPendingLike(t.status));
  };

  // Check that ALL issuance token transfers are approved (gate for redemption)
  const areAllIssuanceTransfersApproved = (bondId) => {
    if (!bondId) return false;

    // Collect issuance-like transfers (support multiple naming variants)
    const allTransfers = getBondTokenTransfers(bondId) || [];
    const issuanceTypeSet = new Set(['issuance', 'issue', 'allocation', 'mint', 'distribution']);
    const issuanceTransfers = allTransfers.filter(t => issuanceTypeSet.has(String(t.transferType || '').toLowerCase()));

    if (!issuanceTransfers || issuanceTransfers.length === 0) return false; // require presence and approval

    // Treat Approved or Completed as approved; block on Pending/Processing/Rejected/Failed/Cancelled
    const isApprovedStatus = (s) => isApprovedLike(s);
    const isBlockingStatus = (s) => {
      const st = String(s).toLowerCase();
      // Treat any pending variant or processing as blocking for issuance completion
      return st.startsWith('pending') || st === 'processing' || st === 'rejected' || st === 'failed' || st === 'cancelled' || st === 'canceled';
    };

    // If any blocking status exists, not all are approved
    if (issuanceTransfers.some(t => isBlockingStatus(t.status))) return false;

    // Ensure every transfer is approved/completed
    return issuanceTransfers.every(t => isApprovedStatus(t.status));
  };

  // Get the status and action for a bond based on token transfers
  const getBondActionStatus = (bond) => {
    const bondId = bond._id || bond.id;

    if (!bondId) {
      return { action: 'none', status: 'Invalid bond ID', disabled: true };
    }

    // Handle bonds that are already in advanced statuses
    const bondStatus = bond.status?.toLowerCase() || '';
    if (bondStatus === BOND_STATUS.REDEMPTION_COMPLETED.toLowerCase() || bondStatus === 'redemption_completed') {
      return {
        action: 'status',
        status: BOND_STATUS.REDEMPTION_COMPLETED,
        disabled: true,
        type: 'success'
      };
    }

    // Check for redemption transfers
    // All redemption transfers approved
    if (areAllRedemptionTransfersApproved(bondId)) {
      return {
        action: 'status',
        status: 'Redemption Completed',
        disabled: true,
        type: 'success'
      };
    }

    // Some approved but not all (partial)
    if (hasApprovedRedemptionTransfers(bondId) && hasPendingRedemptionTransfers(bondId)) {
      return {
        action: 'status',
        status: 'Redemptions Partially Approved',
        disabled: true,
        type: 'pending'
      };
    }

    if (hasPendingRedemptionTransfers(bondId)) {
      return {
        action: 'status',
        status: 'Redemption Submitted, Pending',
        disabled: true,
        type: 'pending'
      };
    }

    // If any redemption transfers (in any other status) already exist, disable prepare action
    if (hasRedemptionTokenTransfers(bondId)) {
      return {
        action: 'redemption',
        status: 'Redemption Already Created',
        disabled: true,
        type: 'info'
      };
    }

    // No redemption transfers found
    if (!hasRedemptionTokenTransfers(bondId)) {
      return {
        action: 'redemption',
        status: 'Prepare Redemption',
        disabled: false,
        type: 'action'
      };
    }

    // Fallback
    return {
      action: 'redemption',
      status: 'Redemption Already Created',
      disabled: true,
      type: 'info'
    };
  };

  // Transform bond data for maturity management
  const maturingBonds = React.useMemo(() => {
    if (!bonds.length) {
      console.log('No bonds available for transformation');
      return [];
    }

    console.log('Transforming bonds:', bonds.length);

    return bonds.map(bond => {
      const currentDate = new Date();
      const maturityDate = bond.maturityDate ? new Date(bond.maturityDate) : null;
      const daysToMaturity = maturityDate ?
        calculateDaysToMaturity(bond.maturityDate) :
        999999; // Large number for bonds without maturity date

      // Determine maturity status based on bond status and maturity date
      let maturityStatus = 'Upcoming';
      const bondStatus = bond.status?.toLowerCase() || '';

      // Handle specific bond statuses first
      if (bondStatus === BOND_STATUS.REDEMPTION_IN_PROCESS.toLowerCase() || bondStatus === 'redemption_in_process') {
        maturityStatus = BOND_STATUS.REDEMPTION_IN_PROCESS;
      } else if (bondStatus === BOND_STATUS.REDEMPTION_COMPLETED.toLowerCase() || bondStatus === 'redemption_completed') {
        maturityStatus = BOND_STATUS.REDEMPTION_COMPLETED;
      } else if (bondStatus === BOND_STATUS.BURN_IN_PROCESS.toLowerCase() || bondStatus === 'burn_in_process') {
        maturityStatus = BOND_STATUS.BURN_IN_PROCESS;
      } else if (bondStatus === BOND_STATUS.BURN_COMPLETED.toLowerCase() || bondStatus === 'burn_completed') {
        maturityStatus = BOND_STATUS.BURN_COMPLETED;
      } else if (bondStatus === BOND_STATUS.ALLOCATION_FINALIZED.toLowerCase() || bondStatus === 'allocation_finalized') {
        maturityStatus = BOND_STATUS.ALLOCATION_FINALIZED;
      } else if (!maturityDate) {
        maturityStatus = 'No Maturity Date';
      } else if (daysToMaturity < 0) {
        maturityStatus = BOND_STATUS.MATURED;
      } else if (daysToMaturity <= 30) {
        maturityStatus = 'Due Soon';
      } else if (daysToMaturity <= 90) {
        maturityStatus = 'Approaching';
      }

      const bondId = bond._id || bond.id;

      // Determine maturity status based on days and conditions
      // No longer checking for burn requests since that's not part of the flow

      return {
        id: bond._id || bond.id || `temp-${Date.now()}-${Math.random()}`,
        _id: bond._id || bond.id, // Keep original _id for API calls
        bondName: bond.bondName || 'Unnamed Bond',
        issuer: bond.issuer?.corporateInfo?.companyName || bond.issuer?.email || 'Unknown Issuer', // Display name
        issuerData: bond.issuer, // Preserve original issuer object for API calls
        issuerId: bond.issuerId || bond.issuer?._id, // Direct issuer ID field
        maturityDate: bond.maturityDate,
        nominalAmount: bond.issueAmount || 0,
        status: maturityStatus,
        daysToMaturity: Math.max(0, daysToMaturity),
        investorCount: bond.numberOfInvestors || 0,
        lastCouponPayment: bond.lastCouponPayment || null,
        totalCouponsPaid: bond.totalCouponsPaid || 0,
        originalIssueDate: bond.issueDate,
        currency: bond.currency || 'MYR',
        couponRate: bond.couponRate || 0,
        productType: bond.productType,
        bondNumber: bond.bondNumber,
        isin: bond.isin,
        // Mock investor data - in real app would come from separate API
        investors: [
          { id: 1, name: 'National Pension Fund', walletAddress: '0x1234...5678', amount: Math.floor(bond.issueAmount * 0.25) },
          { id: 2, name: 'Infrastructure Fund Ltd', walletAddress: '0x2345...6789', amount: Math.floor(bond.issueAmount * 0.20) },
          { id: 3, name: 'Investment Corp Malaysia', walletAddress: '0x3456...7890', amount: Math.floor(bond.issueAmount * 0.15) },
          { id: 4, name: 'Sovereign Wealth Fund', walletAddress: '0x4567...8901', amount: Math.floor(bond.issueAmount * 0.125) }
        ]
      };
    });
  }, [bonds]);

  console.log('Final maturingBonds:', maturingBonds.length);
  console.log('Sample bond:', maturingBonds[0]);

  // Fetch bond redemptions for payment reference check
  useEffect(() => {
    const fetchBondRedemptions = async () => {
      if (maturingBonds.length === 0) return;
      
      const bondIds = maturingBonds
        .map(bond => bond._id || bond.id)
        .filter(id => id && !id.startsWith('temp-'));
      
      if (bondIds.length === 0) return;
      
      try {
        // Fetch redemption data for each bond
        const redemptionPromises = bondIds.map(async (bondId) => {
          try {
            const response = await bondsAPI.getBondRedemptionsByBond(bondId);
            const redemptions = response?.data?.data || response?.data || [];
            // Get the most recent redemption
            const latestRedemption = Array.isArray(redemptions) && redemptions.length > 0 
              ? redemptions.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0]
              : null;
            return { bondId, redemption: latestRedemption };
          } catch (err) {
            console.warn(`Failed to fetch redemption for bond ${bondId}:`, err);
            return { bondId, redemption: null };
          }
        });
        
        const results = await Promise.all(redemptionPromises);
        const redemptionsMap = {};
        results.forEach(({ bondId, redemption }) => {
          if (redemption) {
            redemptionsMap[bondId] = redemption;
          }
        });
        
        setBondRedemptions(redemptionsMap);
      } catch (error) {
        console.error('Failed to fetch bond redemptions:', error);
      }
    };
    
    fetchBondRedemptions();
  }, [maturingBonds.length]);

  // Fetch token transfers when bonds are loaded
  useEffect(() => {
    if (maturingBonds.length > 0) {
      const bondIds = maturingBonds
        .map(bond => bond._id || bond.id)
        .filter(id => id && !id.startsWith('temp-'));

      if (bondIds.length > 0) {
        console.log('🔄 Fetching token transfers for loaded bonds...');
        debouncedFetchTokenTransfers(bondIds);
        // Fetch maturity approval states (batch optimized)
        (async () => {
          setLoadingApprovals(true);
          try {
            let batchResp;
            try {
              batchResp = await maturityApprovalsAPI.getStates(bondIds);
            } catch (batchErr) {
              console.warn('⚠️ Batch maturity approvals endpoint failed or unsupported, falling back to per-bond calls:', batchErr?.message);
            }
            if (batchResp?.data) {
              // Expected shape: { data: { bondId1: { redemption: {...} }, bondId2: {...} } } OR similar
              const payload = batchResp.data.data || batchResp.data;
              if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
                setMaturityApprovals(prev => ({ ...prev, ...payload }));
              } else if (Array.isArray(payload)) {
                // If backend returns array of { bondId, ...state }
                const mapped = payload.reduce((acc, item) => {
                  const id = item?.bondId || item?._id || item?.id;
                  if (id) acc[id] = item;
                  return acc;
                }, {});
                setMaturityApprovals(prev => ({ ...prev, ...mapped }));
              }
            } else {
              // Fallback per-bond fetch
              // Fallback removed per request: do not spam per-bond calls; leave existing cache untouched.
              console.warn('Batch maturity approvals unavailable; skipping per-bond fallback to avoid excess API calls.');
            }
          } catch (e) {
            console.error('❌ Error fetching maturity approvals (batch):', e);
          } finally {
            setLoadingApprovals(false);
          }
        })();
      }
    }
  }, [maturingBonds.length]); // Only depend on length to avoid infinite loops

  // No pre-resolution needed; we will read token address from bond when submitting

  // Manual refresh function
  const handleManualRefresh = async () => {
    console.log('🔄 Manual refresh triggered...');
    setRefreshTrigger(prev => prev + 1);
    await refetch();

    // Also refresh token transfers and bond redemptions
    if (maturingBonds.length > 0) {
      const bondIds = maturingBonds
        .map(bond => bond._id || bond.id)
        .filter(id => id && !id.startsWith('temp-'));

      if (bondIds.length > 0) {
        await fetchTokenTransfers(bondIds);
        
        // Refresh bond redemptions
        try {
          const redemptionPromises = bondIds.map(async (bondId) => {
            try {
              const response = await bondsAPI.getBondRedemptionsByBond(bondId);
              const redemptions = response?.data?.data || response?.data || [];
              const latestRedemption = Array.isArray(redemptions) && redemptions.length > 0 
                ? redemptions.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0]
                : null;
              return { bondId, redemption: latestRedemption };
            } catch (err) {
              return { bondId, redemption: null };
            }
          });
          
          const results = await Promise.all(redemptionPromises);
          const redemptionsMap = {};
          results.forEach(({ bondId, redemption }) => {
            if (redemption) {
              redemptionsMap[bondId] = redemption;
            }
          });
          
          setBondRedemptions(redemptionsMap);
        } catch (error) {
          console.error('Failed to refresh bond redemptions:', error);
        }
      }
    }

    showSuccess('Data refreshed successfully.');
  };

  // Removed auto-refresh on page visibility change per requirement

  // NOTE: Removed previous auto-trigger effect that created redemption transfers immediately after both approvals.
  // Per requirement, creation must occur ONLY once at the moment of L2 approval (see handleRedemptionL2Approve).

  // Handle loading state
  if (isLoading) {
    return <LoadingSpinner size="lg" text="Loading maturity management data..." />;
  }

  // Handle error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-medium text-gray-900 mb-2">Error Loading Data</h2>
          <p className="text-gray-600 mb-4">Failed to load maturity management data. Please try again.</p>
          <button
            onClick={handleManualRefresh}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </button>
        </div>
      </div>
    );
  }



  const getStatusBadge = (status) => {
    const statusConfig = {
      'Upcoming': { color: 'blue', icon: Clock },
      'Approaching': { color: 'yellow', icon: AlertTriangle },
      'Due Soon': { color: 'red', icon: AlertTriangle },
      'No Maturity Date': { color: 'gray', icon: AlertTriangle },
      [BOND_STATUS.ALLOCATION_FINALIZED]: { color: 'blue', icon: CheckCircle },
      [BOND_STATUS.REDEMPTION_IN_PROCESS]: { color: 'orange', icon: RefreshCw },
      [BOND_STATUS.REDEMPTION_COMPLETED]: { color: 'green', icon: CheckCircle },
      [BOND_STATUS.BURN_IN_PROCESS]: { color: 'purple', icon: Flame },
      [BOND_STATUS.BURN_COMPLETED]: { color: 'gray', icon: CheckCircle },
      [BOND_STATUS.MATURED]: { color: 'red', icon: AlertTriangle }
    };

    const config = statusConfig[status] || { color: 'gray', icon: Clock };
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-800`}>
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </span>
    );
  };

  const getUrgencyColor = (daysToMaturity) => {
    if (daysToMaturity <= 7) return 'text-red-600';
    if (daysToMaturity <= 30) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getFilteredBonds = () => {
    return maturingBonds.filter(bond => {
      const matchesSearch = bond.bondName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bond.issuer.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === 'all' || bond.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  };

  const handlePrepareRedemption = async () => {
    console.log('🎯 handlePrepareRedemption called');
    console.log('🗓️ Current redemptionDate state:', redemptionDate);
    console.log('📊 Current redemptionType state:', redemptionType);
    console.log('🔍 Selected bond:', selectedBond?.bondName);

    // Gate by issuance approvals: do not allow if issuance not fully approved
    const preBondId = selectedBond?._id || selectedBond?.id;
    if (!areAllIssuanceTransfersApproved(preBondId)) {
      showError('Issuance token transfers for this bond are not fully approved yet. Please ensure all issuance transfers are Approved before preparing redemption.');
      return;
    }

    if (!selectedBond || !redemptionDate) {
      console.log('❌ Validation failed:');
      console.log('  - selectedBond exists:', !!selectedBond);
      console.log('  - redemptionDate exists:', !!redemptionDate);
      console.log('  - redemptionDate value:', redemptionDate);
      showError('Please select a redemption date.');
      return;
    }

    // Validate redemption date format
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(redemptionDate)) {
      console.error('❌ Invalid redemption date format:', redemptionDate);
      showError('Invalid redemption date format. Please select a valid date.');
      return;
    }

    // Ensure redemption date is not in the past
    const selectedDate = new Date(redemptionDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for comparison

    if (selectedDate < today) {
      console.error('❌ Redemption date is in the past:', redemptionDate);
      showError('Redemption date cannot be in the past. Please select a future date.');
      return;
    }

    console.log('✅ Redemption date validation passed:', redemptionDate);

    try {
      console.log('🔄 Initiating redemption for bond:', selectedBond.bondName);
      console.log('🔍 Selected bond object:', selectedBond);

      // Get bond ID - handle both _id and id fields, with better validation
      const bondId = selectedBond._id || selectedBond.id;

      if (!bondId) {
        console.error('❌ Bond ID is missing from selectedBond:', selectedBond);
        throw new Error('Bond ID is missing. Cannot initiate redemption. Please try refreshing the page.');
      }

      // Additional validation for MongoDB ObjectId format (24 hex characters)
      if (typeof bondId !== 'string' || (bondId.length !== 24 && !bondId.startsWith('temp-'))) {
        console.error('❌ Bond ID format is invalid:', bondId);
        throw new Error('Invalid bond ID format. Please try selecting the bond again.');
      }

      console.log('🆔 Using bond ID:', bondId);

      // Fetch complete bond to read tokenDetails.tokenAddress
      console.log('🔍 Fetching complete bond details to read token address...');
      let tokenContractAddress = null;
      let completeBond = null;
      try {
        const bondResp = await bondsAPI.getBond(bondId);
        const payload = bondResp?.data?.data || bondResp?.data || bondResp;
        completeBond = payload?.bond || payload;
      } catch (addrErr) {
        console.warn('⚠️ Could not fetch complete bond when resolving token address:', addrErr?.message || addrErr);
      }

      // Always use the bond's token address via helper; fallback to selectedBond shape
      tokenContractAddress = getBondTokenAddress(completeBond) || getBondTokenAddress(selectedBond);
      console.log('📦 Token contract address from bond:', tokenContractAddress);
      if (!isValidEthereumAddress(tokenContractAddress)) {
        throw new Error(`Bond token address is missing or invalid for ${selectedBond.bondName || 'bond'} (${bondId}). Please set tokenDetails.tokenAddress on the bond before initiating redemption.`);
      }

      // Don't proceed with temp IDs (these are fallback IDs for bonds without proper IDs)
      if (bondId.startsWith('temp-')) {
        throw new Error('This bond has a temporary ID and cannot be processed. Please contact support.');
      }

      // Two-layer approval gate BEFORE creating transfers.
      // 1. Attempt L1 (if Pending L1) with payload, else if Pending L2 and different user -> prompt L2, else if Approved -> proceed to create transfers.
      let approvalState = maturityApprovals[bondId];
      if (!approvalState) {
        try {
          // Removed on-demand single getState fetch to rely solely on batched data.
          console.log('Skipping on-demand maturity approval fetch for bond', bondId, '— relying on batch cache.');
        } catch (e) {
          console.warn('⚠️ Could not fetch existing approval state, proceeding to create L1:', e.message);
        }
      }

      const redemptionApproval = approvalState?.redemption || { status: 'Pending L1' };
      console.log('🔍 Current redemption approval state:', redemptionApproval);

      // Determine current user id for comparison
      const currentUserId = user?._id || user?.id;
      if (!isValidObjectId(currentUserId)) {
        throw new Error('Invalid current user id; please re-login.');
      }

      // We refactor: do NOT auto-create transfers here anymore.
      // This function now ONLY handles validation and delegates to explicit approval buttons.
      showError('Use the L1 / L2 approval buttons. This legacy handler is deprecated.');
      return;

    } catch (error) {
      console.error('❌ Error preparing redemption:', error);

      // Provide more specific error messages
      let errorMessage = 'Failed to prepare redemption';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      showError(`Failed to initiate redemption: ${errorMessage}`);
    }
  };

  // Explicit L1 approval
  const handleRedemptionL1Approve = async () => {
    if (!selectedBond) return;
    const bondId = selectedBond._id || selectedBond.id;
    
    // Check if payment reference has been uploaded using helper
    if (!hasPaymentReferenceForBond(selectedBond)) {
      showError('Payment reference must be uploaded before L1 approval. Please upload the payment reference first.');
      return;
    }
    
    try {
      const currentUserId = user?._id || user?.id;
      if (!isValidObjectId(currentUserId)) throw new Error('Invalid user id');
      if (!redemptionDate) {
        showError('Select redemption date first.');
        return;
      }
      const payload = { redemptionType, proposedDate: new Date(redemptionDate), notes: `Initiated by ${user?.email || currentUserId}` };
      const resp = await maturityApprovalsAPI.approve(bondId, { action: 'redemption', level: 'L1', payload });
      setMaturityApprovals(prev => ({ ...prev, [bondId]: resp.data?.data }));
      showSuccess('L1 approval recorded. A different user must perform L2.');
      // Close the prepare redemption modal after successful L1 approval
      setShowRedemptionModal(false);
    } catch (e) {
      showError(`Failed L1 approval: ${e.message}`);
    }
  };

  const handleRedemptionL2Approve = async (bond) => {
    const targetBond = bond || selectedBond;
    if (!targetBond) return;
    const bondId = targetBond._id || targetBond.id;
    
    // Check if payment reference has been uploaded using helper
    if (!hasPaymentReferenceForBond(targetBond)) {
      console.error('❌ Payment reference must be uploaded before L2 approval');
      showError('Payment reference must be uploaded before L2 approval. Please upload the payment reference first.');
      return;
    }
    
    try {
      const approvalState = maturityApprovals[bondId];
      const l1User = approvalState?.redemption?.l1ApprovedBy;
      const currentUserId = user?._id || user?.id;
      if (String(l1User) === String(currentUserId)) {
        showError('Different user required for L2.');
        return;
      }
      const resp = await maturityApprovalsAPI.approve(bondId, { action: 'redemption', level: 'L2' });
      const freshApprovalState = resp.data?.data; // capture fresh server-approved state
      setMaturityApprovals(prev => ({ ...prev, [bondId]: freshApprovalState }));
      showSuccess('L2 approval recorded. Creating redemption transfers...');
      // Determine effective date from local state or approval payload / bond maturity
      let effectiveDate = redemptionDate;
      const updated = resp.data?.data?.redemption;
      const candidate = effectiveDate || updated?.proposedDate || updated?.payload?.proposedDate || targetBond.maturityDate;
      if (!effectiveDate && candidate) {
        try { const d = new Date(candidate); if (!isNaN(d.getTime())) { effectiveDate = d.toISOString().split('T')[0]; setRedemptionDate(effectiveDate); } } catch { }
      }
      // Auto-create transfers (guard against duplicate invocation if lock already set)
      if (!redemptionCreationLocks[bondId]) {
        // Pass the fresh approval state to avoid race condition where local state not yet updated
        await createRedemptionTransfers(targetBond, effectiveDate, freshApprovalState);
      } else {
        console.log('🔒 Skipping createRedemptionTransfers after L2 because lock already present for', bondId);
      }
    } catch (e) {
      showError(`Failed L2 approval: ${e.message}`);
    }
  };
  const createRedemptionTransfers = async (bondParam, dateOverride, approvalOverride = null) => {
    const bond = bondParam || selectedBond;
    if (!bond) return;
    const bondId = bond._id || bond.id;
    if (!bondId) return;

    // Ensure we have the latest transfers before deciding (idempotency gate)
    if (!bondTokenTransfers[bondId] || !bondTokenTransfers[bondId].lastFetched) {
      try {
        await fetchTokenTransfers([bondId]);
      } catch (e) {
        console.warn('⚠️ Prefetch token transfers failed before creation attempt', e);
      }
    }

    if (hasRedemptionTokenTransfers(bondId)) {
      console.log('ℹ️ Redemption transfers already exist for bond (post-refresh)', bondId);
      setRedemptionCreationStatus(prev => ({ ...prev, [bondId]: 'success' }));
      // Set a lock so effect will not try again
      setRedemptionCreationLocks(prev => ({ ...prev, [bondId]: true }));
      return;
    }

    const approvalState = approvalOverride || maturityApprovals[bondId];
    if (approvalState?.redemption?.status !== 'Approved') {
      console.log('⚠️ Attempted to create transfers without full approval (maybe stale state)', {
        bondId,
        approvalFromOverride: approvalOverride?.redemption?.status,
        approvalFromState: maturityApprovals[bondId]?.redemption?.status
      });
      return;
    }

    // Check if payment reference has been uploaded using helper
    if (!hasPaymentReferenceForBond(bond)) {
      console.error('❌ Payment reference must be uploaded before creating redemption transfers');
      showError('Payment reference must be uploaded before creating redemption transfers. Please upload the payment reference first.');
      setRedemptionCreationStatus(prev => ({ ...prev, [bondId]: 'failed' }));
      return;
    }

    if (creatingRedemptionTransfers[bondId]) {
      console.log('⏳ Already creating redemption transfers for', bondId);
      return;
    }

    if (redemptionCreationLocks[bondId]) {
      console.log('🔒 Idempotency lock present, skipping creation for', bondId);
      return;
    }

    // Effective date resolution
    let effectiveDate = dateOverride || redemptionDate;
    const rInfo = approvalState.redemption;
    if (!effectiveDate) {
      const candidate = rInfo?.proposedDate || rInfo?.payload?.proposedDate || bond.maturityDate;
      if (candidate) {
        try {
          const d = new Date(candidate);
          if (!isNaN(d.getTime())) {
            effectiveDate = d.toISOString().split('T')[0];
            setRedemptionDate(effectiveDate);
          }
        } catch { }
      }
    }

    if (!effectiveDate) {
      console.warn('⚠️ No effective redemption date; aborting creation');
      return;
    }

    // Set creation flags/locks immediately to prevent re-entry
    setCreatingRedemptionTransfers(prev => ({ ...prev, [bondId]: true }));
    setRedemptionCreationLocks(prev => ({ ...prev, [bondId]: true }));
    setRedemptionCreationStatus(prev => ({ ...prev, [bondId]: 'creating' }));

    try {
      console.log('🔄 Creating redemption transfers using web3 token holders API...');

      // Use the new web3 API to create redemption transfers for all token holders
      const response = await tokenTransfersAPI.createRedemptionTransfersFromWeb3Holders({
        bondId,
        redemptionDate: effectiveDate,
        redemptionType: redemptionType || 'Maturity'
      });

      const result = response?.data?.data || response?.data || response;
      const created = result?.created || 0;
      const failed = result?.failed || 0;
      const transfers = result?.transfers || [];

      console.log(`✅ Web3 redemption creation completed: ${created} created, ${failed} failed`);

      // Optimistically add created transfers to local state
      if (transfers.length > 0) {
        setBondTokenTransfers(prev => {
          const existing = prev[bondId]?.transfers || [];
          return {
            ...prev,
            [bondId]: {
              transfers: [...transfers, ...existing],
              lastFetched: new Date(),
              error: null
            }
          };
        });
      }

      // Refresh token transfers to get the complete updated state
      await fetchTokenTransfers([bondId]);

      const successMessage = created > 0
        ? `Created ${created} redemption transfers from web3 token holders${failed > 0 ? ` (${failed} failed)` : '.'}`
        : 'No token holders found or all redemption transfers already exist.';

      showSuccess(successMessage);

      if (failed > 0) {
        console.warn(`⚠️ ${failed} redemption transfers failed during creation`);
      }

      setRedemptionCreationStatus(prev => ({
        ...prev,
        [bondId]: created > 0 ? 'success' : (failed > 0 ? 'failed' : 'zero')
      }));

    } catch (err) {
      console.error('❌ Error creating redemption transfers from web3 holders', err);
      showError(`Failed to create redemption transfers: ${err.message}`);
      setRedemptionCreationStatus(prev => ({ ...prev, [bondId]: 'failed' }));
    } finally {
      setCreatingRedemptionTransfers(prev => ({ ...prev, [bondId]: false }));
      // Keep lock if transfers exist now; remove if nothing created and none fetched
      const redemptionNow = getBondTokenTransfers(bondId, 'Redemption');
      if (redemptionNow.length === 0) {
        setRedemptionCreationLocks(prev => {
          const cp = { ...prev };
          delete cp[bondId];
          return cp;
        });
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {loadingApprovals && (
        <div className="fixed top-2 right-2 z-50 bg-yellow-100 text-yellow-800 px-3 py-1 rounded shadow text-xs">Loading approvals...</div>
      )}
      {/* Header */}
      <div className="">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Maturity & Redemption Management</h1>
              <p className="mt-2 text-sm text-gray-600">Handle bond maturities and principal redemptions</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white border border-light-gray rounded-lg mb-6">
          <div className="px-4 py-5 sm:p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Search */}
              <div className="md:col-span-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by bond name or issuer..."
                    className="pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Status</option>
                  <option value="Upcoming">Upcoming</option>
                  <option value="Approaching">Approaching</option>
                  <option value="Due Soon">Due Soon</option>
                  <option value={BOND_STATUS.MATURED}>{BOND_STATUS.MATURED}</option>
                  <option value={BOND_STATUS.ALLOCATION_FINALIZED}>{BOND_STATUS.ALLOCATION_FINALIZED}</option>
                  <option value={BOND_STATUS.REDEMPTION_IN_PROCESS}>{BOND_STATUS.REDEMPTION_IN_PROCESS}</option>
                  <option value={BOND_STATUS.REDEMPTION_COMPLETED}>{BOND_STATUS.REDEMPTION_COMPLETED}</option>
                  <option value={BOND_STATUS.BURN_IN_PROCESS}>{BOND_STATUS.BURN_IN_PROCESS}</option>
                  <option value={BOND_STATUS.BURN_COMPLETED}>{BOND_STATUS.BURN_COMPLETED}</option>
                  <option value="No Maturity Date">No Maturity Date</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Maturity Schedule List */}
        <div className="bg-white border border-light-gray rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Maturity Schedule ({total || maturingBonds.length})
              </h3>
              <div className="flex space-x-2">
                <button
                  onClick={() => setRefreshTrigger(prev => prev + 1)}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Refresh
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {getFilteredBonds().map((bond) => (
                <div key={bond.id} className="border border-gray-200 rounded-lg p-6 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-lg font-medium text-gray-900">
                          {bond.bondName}
                        </h4>
                        <div className="flex space-x-2">
                          {bond.subscriptionStatus && getStatusBadge(bond.subscriptionStatus)}
                          {getStatusBadge(bond.status)}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                        <div>
                          <p className="text-sm text-gray-500">Issuer</p>
                          <div className="flex items-center mt-1">
                            <User className="h-4 w-4 text-gray-400 mr-1" />
                            <p className="text-sm font-medium text-gray-900">{bond.issuer}</p>
                          </div>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Maturity Date</p>
                          <div className="flex items-center mt-1">
                            <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                            <p className="text-sm font-medium text-gray-900">{formatDate(bond.maturityDate)}</p>
                          </div>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Nominal Amount</p>
                          <div className="flex items-center mt-1">
                            <DollarSign className="h-4 w-4 text-gray-400 mr-1" />
                            <p className="text-sm font-medium text-gray-900">{formatCurrency(bond.nominalAmount)}</p>
                          </div>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Days to Maturity</p>
                          <p className={`text-sm font-medium mt-1 ${getUrgencyColor(bond.daysToMaturity > 999999 ? 0 : bond.daysToMaturity)}`}>
                            {bond.daysToMaturity > 999999 ? 'No Date Set' : `${Math.max(0, bond.daysToMaturity)} days`}
                          </p>
                        </div>
                      </div>

                      <div className="mb-4">
                        <p className="text-sm text-gray-500">
                          {bond.investorCount} investors holding tokens
                        </p>
                      </div>

                      {/* Payment Reference Status */}
                      <div className="mb-4">
                        <div className="flex items-center space-x-2 text-xs">
                          {hasPaymentReferenceForBond(bond) ? (
                            <span className="px-2 py-1 rounded-full bg-green-100 text-green-700 border border-green-200 flex items-center">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Payment Reference Uploaded
                            </span>
                          ) : (
                            <span className="px-2 py-1 rounded-full bg-yellow-100 text-yellow-700 border border-yellow-200 flex items-center">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Payment Reference Required
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Redemption Approval Status (L1 / L2) */}
                      {(() => {
                        const bondId = bond._id || bond.id;
                        const approval = maturityApprovals[bondId]?.redemption;
                        if (!approval) {
                          return (
                            <div className="mb-4">
                              <div className="flex items-center space-x-2 text-xs">
                                <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200">Redemption: Pending L1</span>
                              </div>
                            </div>
                          );
                        }
                        const fmtTime = (d) => {
                          try { if (!d) return ''; const dt = new Date(d); if (isNaN(dt.getTime())) return ''; return dt.toLocaleString(); } catch { return ''; }
                        };
                        const l1Approved = approval.status !== 'Pending L1' && !!approval.l1ApprovedBy;
                        const l2Approved = approval.status === 'Approved' && !!approval.l2ApprovedBy;
                        return (
                          <div className="mb-4">
                            <div className="flex items-start flex-wrap gap-2 text-[11px] leading-tight">
                              <span className="px-2 py-0.5 rounded-full border bg-gray-50 text-gray-700">
                                Redemption: {approval.status}
                              </span>
                              {l1Approved && (
                                <span className="px-2 py-0.5 rounded-full border border-blue-200 bg-blue-50 text-blue-700" title={fmtTime(approval.l1ApprovedAt)}>
                                  L1: {(approval.l1ApprovedByEmail || approval.l1ApprovedBy || '').toString().slice(0, 40)} {fmtTime(approval.l1ApprovedAt) && `@ ${fmtTime(approval.l1ApprovedAt)}`}
                                </span>
                              )}
                              {!l1Approved && (
                                <span className="px-2 py-0.5 rounded-full border border-yellow-200 bg-yellow-50 text-yellow-700">L1 Pending</span>
                              )}
                              {approval.status === 'Pending L2' && l1Approved && !l2Approved && (
                                <span className="px-2 py-0.5 rounded-full border border-indigo-200 bg-indigo-50 text-indigo-700" title="Awaiting different user for L2">L2 Pending</span>
                              )}
                              {l2Approved && (
                                <span className="px-2 py-0.5 rounded-full border border-green-200 bg-green-50 text-green-700" title={fmtTime(approval.l2ApprovedAt)}>
                                  L2: {(approval.l2ApprovedByEmail || approval.l2ApprovedBy || '').toString().slice(0, 40)} {fmtTime(approval.l2ApprovedAt) && `@ ${fmtTime(approval.l2ApprovedAt)}`}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Subscription Period Info */}
                      {bond.subscriptionStart && bond.subscriptionEnd && (
                        <div className="mb-4 p-3 bg-blue-50 rounded border-l-4 border-blue-400">
                          <div className="flex items-center mb-2">
                            <Clock className="h-4 w-4 text-blue-600 mr-2" />
                            <h5 className="text-sm font-medium text-blue-800">Subscription Period</h5>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-blue-700">
                            <div>
                              <span className="font-medium">Opens:</span> {formatDate(bond.subscriptionStart)}
                              {bond.subscriptionStatus === 'Upcoming Subscription' && (
                                <span className="ml-2 text-blue-600">({bond.daysToSubscriptionStart} days)</span>
                              )}
                            </div>
                            <div>
                              <span className="font-medium">Closes:</span> {formatDate(bond.subscriptionEnd)}
                              {bond.subscriptionStatus === 'Active Subscription' && (
                                <span className="ml-2 text-blue-600">({bond.daysToSubscriptionEnd} days)</span>
                              )}
                            </div>
                          </div>
                          {bond.subscriptionStatus === 'Active Subscription' && (
                            <div className="mt-2 text-sm font-medium text-green-700">
                              🟢 Currently accepting subscriptions
                            </div>
                          )}
                          {bond.subscriptionStatus === 'Subscription Closed' && (
                            <div className="mt-2 text-sm font-medium text-gray-600">
                              🔴 Subscription period has ended
                            </div>
                          )}
                        </div>
                      )}

                      {/* Show loading indicator for token transfers */}
                      {loadingTokenTransfers && (
                        <div className="mb-4 p-3 bg-yellow-50 rounded border-l-4 border-yellow-400">
                          <div className="flex items-center">
                            <RefreshCw className="h-4 w-4 text-yellow-600 mr-2 animate-spin" />
                            <p className="text-sm text-yellow-800">
                              Checking token transfer status...
                            </p>
                          </div>
                        </div>
                      )}

                      {bond.redemptionCompleted && bond.tokensBurned && (
                        <div className="mb-4 p-3 bg-green-50 rounded">
                          <p className="text-sm text-green-800">
                            Redemption completed on {formatDate(bond.redemptionCompleted)} •
                            Tokens burned on {formatDate(bond.tokensBurned)}
                          </p>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => {
                            console.log('👁️ View Details clicked for bond:', bond);

                            // Additional safety check
                            if (!bond || (!bond._id && !bond.id)) {
                              console.error('❌ Invalid bond selected:', bond);
                              showError('Unable to view details: Invalid bond data. Please refresh the page and try again.');
                              return;
                            }

                            setSelectedBond(bond);
                            setShowDetailModal(true);
                          }}
                          disabled={!bond || (!bond._id && !bond.id)}
                          className="btn-secondary"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View Details
                        </button>

                        {/* Dynamic Action Button Based on Token Transfer Status */}
                        {(() => {
                          const actionStatus = getBondActionStatus(bond);

                          if (actionStatus.action === 'redemption') {
                            const bondId = bond._id || bond.id;
                            const issuanceReady = areAllIssuanceTransfersApproved(bondId);
                            const approvalState = maturityApprovals[bondId]?.redemption; // may be undefined or { status: 'Pending L1', ... }
                            const currentUserId = user?._id || user?.id;
                            // Determine if user has "initiated" (i.e., selected a date) but not yet submitted L1.
                            const proposedDate = approvalState?.proposedDate || approvalState?.payload?.proposedDate;
                            const isInitialPendingL1 = (approvalState?.status === 'Pending L1' && !proposedDate);
                            const issuanceDataFetched = !!bondTokenTransfers[bondId];
                            const basePrepareDisabled = (!issuanceReady && issuanceDataFetched);
                            if (!approvalState || isInitialPendingL1) {
                              // Show initial Prepare Redemption button
                              return (
                                <>
                                  <button
                                    onClick={() => {
                                      if (basePrepareDisabled) return;
                                      
                                      // Check if payment reference has been uploaded using helper
                                      if (!hasPaymentReferenceForBond(bond)) {
                                        showError('Payment reference must be uploaded before preparing redemption. Please upload the payment reference first.');
                                        return;
                                      }
                                      
                                      setSelectedBond(bond);
                                      let prefillDate = '';
                                      if (bond.maturityDate) {
                                        try { const d = new Date(bond.maturityDate); if (!isNaN(d.getTime())) prefillDate = d.toISOString().split('T')[0]; } catch { }
                                      }
                                      setRedemptionDate(prefillDate);
                                      setShowRedemptionModal(true);
                                    }}
                                    disabled={basePrepareDisabled}
                                    title={basePrepareDisabled ? (!issuanceReady && issuanceDataFetched ? 'Issuance transfers not fully approved yet.' : 'You lack permission.') : 'Open modal to set redemption date'}
                                    className="btn-primary "
                                  >
                                    Prepare Redemption
                                  </button>
                                  {(!issuanceReady && !bondTokenTransfers[bondId]) && (
                                    <span className="text-xs text-gray-500 ml-2">(Verifying issuance status...)</span>
                                  )}
                                  {(!issuanceReady && bondTokenTransfers[bondId]) && (
                                    <span className="text-xs text-red-600 ml-2">Issuance not fully approved</span>
                                  )}
                                </>
                              );
                            }
                            // If we have a proposed date captured but still Pending L1 -> show Approve L1
                            if (approvalState.status === 'Pending L1' && proposedDate) {
                              return (
                                <button
                                  onClick={() => {
                                    // Ensure local state reflects proposed date for L1 submission in modal logic
                                    if (!redemptionDate && proposedDate) {
                                      try { const d = new Date(proposedDate); if (!isNaN(d.getTime())) setRedemptionDate(d.toISOString().split('T')[0]); } catch { }
                                    }
                                    setSelectedBond(bond);
                                    setShowRedemptionModal(true); // user can review and submit L1 inside modal
                                  }}
                                  disabled={!issuanceReady}
                                  title={!issuanceReady ? 'Issuance transfers not fully approved yet.' : 'Review and submit L1 approval'}
                                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                                >
                                  Approve L1
                                </button>
                              );
                            }
                            // Pending L2
                            if (approvalState.status === 'Pending L2') {
                              const isL1User = String(approvalState.l1ApprovedBy) === String(currentUserId);
                              if (isL1User) {
                                return (
                                  <button
                                    disabled
                                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md bg-gray-100 text-gray-500 cursor-not-allowed"
                                    title="Waiting for different user to perform L2"
                                  >
                                    Awaiting L2
                                  </button>
                                );
                              }
                              return (
                                <button
                                  onClick={() => {
                                    // Open modal for L2 reviewer to inspect details before approving
                                    setSelectedBond(bond);
                                    // Prefill redemption data from approval if available (L1 already done)
                                    try {
                                      const proposed = approvalState?.proposedDate || approvalState?.payload?.proposedDate;
                                      if (proposed) {
                                        const d = new Date(proposed);
                                        if (!isNaN(d.getTime())) setRedemptionDate(d.toISOString().split('T')[0]);
                                      }
                                      const rType = approvalState?.payload?.redemptionType;
                                      if (rType) setRedemptionType(rType);
                                    } catch { }
                                    setShowRedemptionModal(true);
                                  }}
                                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                                  title="Review details then perform L2 approval"
                                >
                                  Approve L2
                                </button>
                              );
                            }
                            // Approved but no transfers yet
                            if (approvalState.status === 'Approved' && !hasRedemptionTokenTransfers(bondId)) {
                              const creating = creatingRedemptionTransfers[bondId];
                              return (
                                <div className="inline-flex items-center px-3 py-2 border border-green-300 text-sm leading-4 font-medium rounded-md bg-green-50 text-green-700">
                                  {(() => {
                                    if (creating) return 'Creating Transfers...';
                                    const s = redemptionCreationStatus[bondId];
                                    if (s === 'success') return 'Redemption Transfers Created';
                                    if (s === 'zero') return 'Approved (No Allocations)';
                                    if (s === 'failed') return 'Creation Failed (Retry Manually)';
                                    return 'Approved (Creating...)';
                                  })()}
                                </div>
                              );
                            }
                            // If transfers created or other state, fallback disabled prepare
                            return (
                              <button
                                disabled
                                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md bg-gray-100 text-gray-500 cursor-not-allowed"
                              >
                                Prepare Redemption
                              </button>
                            );
                          } else if (actionStatus.action === 'status') {
                            // Show status badge
                            const badgeClasses = {
                              pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
                              success: 'bg-green-100 text-green-800 border-green-200'
                            };

                            return (
                              <div className={`inline-flex items-center px-3 py-2 border text-sm leading-4 font-medium rounded-md ${badgeClasses[actionStatus.type] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
                                {actionStatus.type === 'pending' && <Clock className="h-4 w-4 mr-1" />}
                                {actionStatus.type === 'success' && <CheckCircle className="h-4 w-4 mr-1" />}
                                {actionStatus.status}
                              </div>
                            );
                          }

                          return null;
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {getFilteredBonds().length === 0 && (
                <div className="text-center py-12">
                  <Calendar className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No bonds found</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    No bonds match your current filter criteria.
                  </p>
                </div>
              )}
            </div>

            {/* Pagination Controls */}
            {maturingBonds.length > 0 && (
              <div className="px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                <div className="text-xs text-gray-600">
                  Page {page} of {totalPages} • {total} bond{total === 1 ? '' : 's'} total • Showing {maturingBonds.length} on this page
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1 || isLoading}
                    className="px-3 py-1.5 text-xs rounded-md border border-gray-300 bg-white text-gray-700 disabled:opacity-50 hover:bg-gray-50"
                  >Prev</button>

                  {/* Page number buttons */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        disabled={pageNum === page || isLoading}
                        className={`px-3 py-1.5 text-xs rounded-md border ${pageNum === page
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 disabled:opacity-50'
                          }`}
                      >{pageNum}</button>
                    );
                  })}

                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages || isLoading}
                    className="px-3 py-1.5 text-xs rounded-md border border-gray-300 bg-white text-gray-700 disabled:opacity-50 hover:bg-gray-50"
                  >Next</button>

                  <select
                    value={pageSize}
                    onChange={e => {
                      setPage(1);
                      setPageSize(parseInt(e.target.value));
                    }}
                    disabled={isLoading}
                    className="ml-2 text-xs border-gray-300 rounded-md px-2 py-1 bg-white text-gray-700"
                  >
                    {[10, 20, 50, 100].map(sz => <option key={sz} value={sz}>{sz}/page</option>)}
                  </select>

                  {isLoading && (
                    <span className="ml-2 text-xs text-gray-500 flex items-center">
                      <RefreshCw className="h-3 w-3 animate-spin mr-1" /> Loading...
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bond Detail Modal */}
      {showDetailModal && selectedBond && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4 pb-4 px-6">
                <h3 className="text-lg font-medium text-gray-900">Bond Maturity Details</h3>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="border-b border-light-gray mb-6"></div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Bond Information</h4>
                  <dl className="space-y-2">
                    <div>
                      <dt className="text-xs text-gray-500">Bond Name</dt>
                      <dd className="text-sm text-gray-900">{selectedBond.bondName}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Issuer</dt>
                      <dd className="text-sm text-gray-900">{selectedBond.issuer}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Original Issue Date</dt>
                      <dd className="text-sm text-gray-900">{formatDate(selectedBond.originalIssueDate)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Maturity Date</dt>
                      <dd className="text-sm text-gray-900">{formatDate(selectedBond.maturityDate)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Nominal Amount</dt>
                      <dd className="text-sm text-gray-900">{formatCurrency(selectedBond.nominalAmount)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Days to Maturity</dt>
                      <dd className={`text-sm font-medium ${getUrgencyColor(selectedBond.daysToMaturity > 999999 ? 0 : selectedBond.daysToMaturity)}`}>
                        {selectedBond.daysToMaturity > 999999 ? 'No Date Set' : `${Math.max(0, selectedBond.daysToMaturity)} days`}
                      </dd>
                    </div>
                  </dl>
                </div>



                {selectedBond.subscriptionStart && selectedBond.subscriptionEnd && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Subscription Period</h4>
                    <dl className="space-y-2">
                      <div>
                        <dt className="text-xs text-gray-500">Subscription Start</dt>
                        <dd className="text-sm text-gray-900">{formatDate(selectedBond.subscriptionStart)}</dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-500">Subscription End</dt>
                        <dd className="text-sm text-gray-900">{formatDate(selectedBond.subscriptionEnd)}</dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-500">Subscription Status</dt>
                        <dd className="text-sm text-gray-900">{getStatusBadge(selectedBond.subscriptionStatus)}</dd>
                      </div>
                      {selectedBond.subscriptionCountdown && (
                        <div>
                          <dt className="text-xs text-gray-500">
                            {selectedBond.subscriptionStatus === 'Upcoming Subscription' ? 'Opens In' :
                              selectedBond.subscriptionStatus === 'Active Subscription' ? 'Closes In' : 'Countdown'}
                          </dt>
                          <dd className="text-sm font-medium text-blue-600">
                            {selectedBond.subscriptionCountdown} days
                          </dd>
                        </div>
                      )}
                    </dl>
                  </div>
                )}

                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Payment History</h4>
                  <dl className="space-y-2">
                    <div>
                      <dt className="text-xs text-gray-500">Last Payment Date</dt>
                      <dd className="text-sm text-gray-900">
                        {formatDate(selectedBond.lastCouponPayment || selectedBond.lastProfitPayment)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Total Payments Made</dt>
                      <dd className="text-sm text-gray-900">
                        {selectedBond.totalCouponsPaid || selectedBond.totalProfitsPaid} payments
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Current Status</dt>
                      <dd className="text-sm text-gray-900">{getStatusBadge(selectedBond.status)}</dd>
                    </div>
                  </dl>
                </div>
              </div>

              <div className="mt-6">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Investor Redemption Breakdown</h4>
                <div className="max-h-40 overflow-y-auto">
                  {selectedBond.investors?.map((investor) => (
                    <div key={investor.id} className="flex justify-between py-2 border-b border-gray-100">
                      <div>
                        <p className="text-xs font-medium text-gray-900">{investor.name}</p>
                        <p className="text-xs text-gray-500">{investor.walletAddress}</p>
                      </div>
                      <p className="text-xs font-medium text-gray-900">{formatCurrency(investor.amount)}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Redemption Preparation Modal */}
      {showRedemptionModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              {selectedBond ? (
                <>
                  <div className="flex items-center justify-between mb-4 pb-4 px-5">
                    <h3 className="text-lg font-medium text-gray-900">Prepare Redemption</h3>
                    <button
                      onClick={() => {
                        setShowRedemptionModal(false);
                        setRedemptionType('Maturity');
                        setRedemptionDate('');
                        setSelectedBond(null);
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>
                  <div className="border-b border-light-gray mb-4"></div>

                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>Bond:</strong> {selectedBond.bondName || 'Unknown Bond'}
                    </p>
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>Maturity Date:</strong> {formatDate(selectedBond.maturityDate)}
                    </p>
                    <p className="text-sm text-gray-600 mb-4">
                      <strong>Principal Amount:</strong> {formatCurrency(selectedBond.nominalAmount || 0)}
                    </p>
                    <p className="text-xs text-gray-500 mb-4">
                      <strong>Bond ID:</strong> {selectedBond._id || selectedBond.id || 'No ID available'}
                    </p>
                  </div>

                  {/* Redemption Type Selection */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Redemption Type
                    </label>
                    <select
                      value={redemptionType}
                      onChange={(e) => setRedemptionType(e.target.value)}
                      disabled={(() => { const bId = selectedBond._id || selectedBond.id; const st = maturityApprovals[bId]?.redemption; return st && st.status !== 'Pending L1'; })()}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <option value="Maturity">Maturity</option>
                      <option value="Early Redemption">Early Redemption</option>
                    </select>
                    {(() => { const bId = selectedBond._id || selectedBond.id; const st = maturityApprovals[bId]?.redemption; if (st && st.status !== 'Pending L1') return <p className="mt-1 text-xs text-gray-500">Locked after L1 approval.</p>; return null; })()}
                  </div>

                  {/* Redemption Date Selection */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Redemption Date
                    </label>
                    <input
                      type="date"
                      value={redemptionDate}
                      onChange={(e) => {
                        console.log('📅 Redemption date changed:', e.target.value);
                        setRedemptionDate(e.target.value);
                      }}
                      min={new Date().toISOString().split('T')[0]}
                      disabled={(() => { const bId = selectedBond._id || selectedBond.id; const st = maturityApprovals[bId]?.redemption; return st && st.status !== 'Pending L1'; })()}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                    {(() => { const bId = selectedBond._id || selectedBond.id; const st = maturityApprovals[bId]?.redemption; if (st && st.status !== 'Pending L1') return <p className="mt-1 text-xs text-gray-500">Date locked after L1 approval. L2 can only review.</p>; return null; })()}
                  </div>

                  <div className="bg-blue-50 p-3 rounded mb-4">
                    <p className="text-sm text-blue-800">
                      This will initiate the redemption process for {selectedBond.investorCount || 0} investors.
                      Individual redemptions will be created with "Pending Approval" status and sent directly to the issuer for review.
                    </p>
                    <p className="text-xs text-gray-600 mt-2">
                      Current redemption date: {redemptionDate || 'Not set'}
                    </p>
                    {(() => {
                      const bondId = selectedBond._id || selectedBond.id;
                      const st = maturityApprovals[bondId]?.redemption;
                      if (!st) return <p className="text-xs mt-2 text-gray-500">Approval Status: Pending L1</p>;
                      return (
                        <div className="mt-2 text-xs space-y-1">
                          <p>Approval Status: <span className="font-semibold">{st.status}</span></p>
                          {st.l1ApprovedBy && <p>L1: {st.l1ApprovedByEmail || st.l1ApprovedBy} at {st.l1ApprovedAt && new Date(st.l1ApprovedAt).toLocaleString()}</p>}
                          {st.l2ApprovedBy && <p>L2: {st.l2ApprovedByEmail || st.l2ApprovedBy} at {st.l2ApprovedAt && new Date(st.l2ApprovedAt).toLocaleString()}</p>}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Token contract address is taken directly from the bond; no manual input */}

                  {(() => {
                    const bondId = selectedBond._id || selectedBond.id;
                    const st = maturityApprovals[bondId]?.redemption || { status: 'Pending L1' };
                    const currentUserId = user?._id || user?.id;
                    const l1User = st.l1ApprovedBy;
                    return (
                      <div className="flex flex-col space-y-2">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => {
                              setShowRedemptionModal(false);
                              setRedemptionType('Maturity');
                              setRedemptionDate('');
                              setSelectedBond(null);
                            }}
                            className="btn-secondary"
                          >Cancel</button>
                          {st.status === 'Pending L1' && (
                            <button
                              onClick={handleRedemptionL1Approve}
                              disabled={!redemptionDate}
                              className="btn-primary"
                            >Submit L1 Approval</button>
                          )}
                          {st.status === 'Pending L2' && String(l1User) !== String(currentUserId) && (
                            <button
                              onClick={async () => {
                                await handleRedemptionL2Approve();
                                // Close modal after triggering L2 (transfers auto-create separately)
                                setShowRedemptionModal(false);
                              }}
                              className="px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm"
                            >Approve L2</button>
                          )}
                          {st.status === 'Pending L2' && String(l1User) === String(currentUserId) && (
                            <span className="px-2 py-2 text-xs bg-yellow-100 text-yellow-700 rounded">Waiting for different user for L2</span>
                          )}
                          {st.status === 'Approved' && (
                            <div className="px-3 py-2 bg-green-50 text-green-700 rounded-md text-sm border border-green-300">
                              {(() => {
                                if (creatingRedemptionTransfers[bondId]) return 'Creating Redemption Transfers...';
                                const status = redemptionCreationStatus[bondId];
                                if (status === 'success') return 'Redemption Transfers Created';
                                if (status === 'zero') return 'Approved (No Allocations)';
                                if (status === 'failed') return 'Approved (Creation Failed - Retry Pending)';
                                return 'Approved (Creating...)';
                              })()}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </>
              ) : (
                <div className="text-center py-4">
                  <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Bond Data Missing</h3>
                  <p className="text-gray-600 mb-4">The selected bond information is not available. Please try again.</p>
                  <button
                    onClick={() => {
                      setShowRedemptionModal(false);
                      setSelectedBond(null);
                    }}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">Error</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  {errorMessage}
                </p>
              </div>
              <div className="flex justify-center mt-4">
                <button
                  onClick={() => {
                    setShowErrorModal(false);
                    setErrorMessage('');
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">Success!</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500 whitespace-pre-line">
                  {successMessage}
                </p>
              </div>
              <div className="flex justify-center mt-4">
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    setSuccessMessage('');
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                >
                  Got it!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default EarlyRedemption; 